import { useState } from 'react';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetUserSpecific3DsTx } from '@package/smart-wallet';
import { useForm } from 'react-hook-form';
import ReactJson from 'react-json-view';
import { z } from 'zod';

import { LoaderSkeleton } from './LoaderSkeleton';

/* Schemas */
export const UserSpecific3DsTransactionFormSchema = z.object({
  cardId: z.string().refine((value) => !value || !isNaN(Number(value)), {
    message: 'Card Id must be a valid number',
  }),
  tradeNo: z.string().refine((value) => !value || !isNaN(Number(value)), {
    message: 'Trade number must be a valid number',
  }),
});

/* Types */
export type UserSpecific3DsTransactionFormType = z.infer<
  typeof UserSpecific3DsTransactionFormSchema
>;

/* Components */
export const UserSpecific3DsTransaction = () => {
  const [cardId, setCardId] = useState<number | undefined>(undefined);
  const [tradeNo, setTradeNo] = useState<string | undefined>(undefined);

  /* Search Form */
  const searchForm = useForm<UserSpecific3DsTransactionFormType>({
    resolver: zodResolver(UserSpecific3DsTransactionFormSchema),
  });

  /* Handlers */
  const handleSearch = async (data: UserSpecific3DsTransactionFormType) => {
    setTradeNo(data.tradeNo);
    setCardId(Number(data.cardId));
  };

  /* Render */
  return (
    <section className='w-2/3 flex flex-col items-center justify-center gap-4'>
      <Form {...searchForm}>
        <form
          onSubmit={searchForm.handleSubmit(handleSearch)}
          className='w-full flex flex-col items-center justify-center gap-4'
        >
          <section className='w-full flex flex-row items-center justify-center gap-4'>
            <FormField
              control={searchForm.control}
              name='cardId'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>Card Id</FormLabel>
                  <FormControl>
                    <Input placeholder='1' type='number' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={searchForm.control}
              name='tradeNo'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>Trade No</FormLabel>
                  <FormControl>
                    <Input placeholder='1' type='number' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </section>
          <Button type='submit' className='cursor-pointer w-full'>
            Get specific 3ds transaction
          </Button>
        </form>
      </Form>
      {!!cardId && !!tradeNo && (
        <GetUserSpecific3DsTransaction cardId={cardId} tradeNo={tradeNo} />
      )}
    </section>
  );
};

/*Internal components */
type GetUserSpecific3DsTransactionProps = {
  cardId: number;
  tradeNo: string;
};

const GetUserSpecific3DsTransaction = ({
  cardId,
  tradeNo,
}: GetUserSpecific3DsTransactionProps) => {
  const { isLoading, record } = useGetUserSpecific3DsTx({
    cardId,
    tradeNo: String(tradeNo),
  });

  /* Render */
  return (
    <section className='w-full flex flex-col items-center justify-center'>
      <section className='w-full flex flex-col items-center justify-center gap-4'>
        {!!isLoading && <LoaderSkeleton />}
        {!isLoading && record && (
          <section className='w-full flex flex-col items-start justify-start gap-2'>
            <ReactJson collapsed src={record || {}} />
          </section>
        )}
      </section>
    </section>
  );
};
